// File: frontend/src/context/AuthContext.js

import React, { createContext, useState, useEffect, useContext } from 'react';
import { jwtDecode } from 'jwt-decode';
import api from '../utils/axios';

// Create React context for Authentication
export const AuthContext = createContext(null);

// AuthProvider component to wrap application and manage authentication state
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token') || null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state on initial mount from local storage
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedToken = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');

        if (storedToken && storedUser) {
          const decodedToken = jwtDecode(storedToken);
          const currentTime = Date.now() / 1000;

          // Check if token expiration exists and is expired
          if (decodedToken.exp && decodedToken.exp < currentTime) {
            console.warn('Authentication token expired. Redirecting to login.');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setToken(null);
            setUser(null);
          } else {
            setToken(storedToken);
            setUser(JSON.parse(storedUser));
          }
        } else {
          setToken(null);
          setUser(null);
        }
      } catch (error) {
        // If error parsing or decoding stored token, reset auth state
        console.error('Failed to parse or decode stored authentication token:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Login function to authenticate user with API
  const login = async (email, password) => {
    try {
      // Send login request to backend API
      const response = await api.post('/api/v1/auth/login', { email, password });
      
      const responseData = response.data;
      const authToken = responseData.token || responseData.data?.token;
      const userData = responseData.user || responseData.data?.user || responseData.data;

      if (!authToken) {
        throw new Error('Token not received from server');
      }

      // Save token and user info to localStorage
      localStorage.setItem('token', authToken);
      localStorage.setItem('user', JSON.stringify(userData));

      // Update auth context state
      setToken(authToken);
      setUser(userData);

      return { success: true, user: userData };
    } catch (error) {
      // Catch and format error message
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Login failed. Please check credentials.';
      return { success: false, error: errorMessage };
    }
  };

  // Logout function to clear session and state
  const logout = async () => {
    try {
      // Clear token and user from local storage
      localStorage.removeItem('token');
      localStorage.removeItem('user');

      // Reset state
      setToken(null);
      setUser(null);
    } catch (error) {
      // Catch error during logout process
      console.error('Logout error:', error);
    }
  };

  // Function to check if user is authenticated
  const isAuthenticated = () => {
    try {
      const activeToken = token || localStorage.getItem('token');
      if (!activeToken) return false;

      const decoded = jwtDecode(activeToken);
      return decoded.exp ? decoded.exp > (Date.now() / 1000) : true;
    } catch (error) {
      console.error('Authentication check error:', error);
      return false;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        logout,
        isAuthenticated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use AuthContext
export const useAuth = () => {
  try {
    const context = useContext(AuthContext);
    if (!context) {
      throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
  } catch (error) {
    console.error('useAuth hook error:', error);
    throw error;
  }
};
